import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit2, Plus, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Input, Label, Row } from "@/Components/ui";
import { groupReservationService } from "@/api/services/group-reservation.service";
import { GroupReservationOptionProduct, GroupReservationOptionProductPayload } from "@/api/types/group-reservation";
import { dialogService } from "@/utils/dialogService";
import GroupReservationPageShell from "../GroupReservationPageShell";

const emptyForm = (): GroupReservationOptionProductPayload => ({
  name: "",
  description: "",
  weekday_price: 0,
  weekend_price: 0,
  is_active: true,
  sort_order: 0,
});

const formatAmount = (value?: number | null) => (value ? `${Number(value).toLocaleString()}원` : "0원");

export default function GroupReservationOptionProductsPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<GroupReservationOptionProductPayload>(emptyForm());

  const optionQuery = useQuery({
    queryKey: ["group-reservation", "option-products"],
    queryFn: () => groupReservationService.getOptionProducts(),
  });

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        name: form.name.trim(),
        description: form.description?.trim() || null,
        weekday_price: Number(form.weekday_price || 0),
        weekend_price: Number(form.weekend_price || 0),
        sort_order: Number(form.sort_order || 0),
      };
      if (editingId) {
        return groupReservationService.updateOptionProduct(editingId, payload);
      }
      return groupReservationService.createOptionProduct(payload);
    },
    onSuccess: () => {
      toast.success(editingId ? "단체예약 옵션을 수정했습니다." : "단체예약 옵션을 추가했습니다.");
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["group-reservation", "option-products"] });
    },
    onError: (error: any) => toast.error(error?.message || "단체예약 옵션 저장에 실패했습니다."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => groupReservationService.deleteOptionProduct(id),
    onSuccess: () => {
      toast.success("단체예약 옵션을 삭제했습니다.");
      queryClient.invalidateQueries({ queryKey: ["group-reservation", "option-products"] });
    },
    onError: (error: any) => toast.error(error?.message || "단체예약 옵션 삭제에 실패했습니다."),
  });

  const options = optionQuery.data?.data.results ?? [];

  const startEdit = (option: GroupReservationOptionProduct) => {
    setEditingId(option.id);
    setForm({
      name: option.name,
      description: option.description || "",
      weekday_price: option.weekday_price,
      weekend_price: option.weekend_price,
      is_active: option.is_active,
      sort_order: option.sort_order,
    });
  };

  const submit = () => {
    if (!form.name.trim()) {
      toast.error("옵션명을 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  return (
    <GroupReservationPageShell>
      <BreadCrumb title="단체예약 옵션 상품" pageTitle="예약" />

      <Row className="g-3">
        <Col xl={4}>
          <Card>
            <CardHeader>{editingId ? "옵션 수정" : "옵션 추가"}</CardHeader>
            <CardBody>
              <div className="mb-3">
                <Label htmlFor="group-option-name">옵션명</Label>
                <Input
                  id="group-option-name"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </div>
              <div className="mb-3">
                <Label htmlFor="group-option-description">설명</Label>
                <Input
                  id="group-option-description"
                  type="textarea"
                  rows={3}
                  value={form.description || ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <Label htmlFor="group-option-weekday">주중 가격</Label>
                  <Input
                    id="group-option-weekday"
                    type="number"
                    min="0"
                    value={form.weekday_price}
                    onChange={(event) => setForm((prev) => ({ ...prev, weekday_price: Number(event.target.value) }))}
                  />
                </Col>
                <Col md={6}>
                  <Label htmlFor="group-option-weekend">주말 가격</Label>
                  <Input
                    id="group-option-weekend"
                    type="number"
                    min="0"
                    value={form.weekend_price}
                    onChange={(event) => setForm((prev) => ({ ...prev, weekend_price: Number(event.target.value) }))}
                  />
                </Col>
                <Col md={6}>
                  <Label htmlFor="group-option-order">정렬</Label>
                  <Input
                    id="group-option-order"
                    type="number"
                    min="0"
                    value={form.sort_order}
                    onChange={(event) => setForm((prev) => ({ ...prev, sort_order: Number(event.target.value) }))}
                  />
                </Col>
                <Col md={6}>
                  <Label className="d-block">노출</Label>
                  <label className="d-flex align-items-center gap-2 mt-2">
                    <Input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                    />
                    <span>고객 신청 화면에 표시</span>
                  </label>
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button onClick={submit} disabled={saveMutation.isPending}>
                  <Plus size={16} className="me-1" />
                  {editingId ? "수정" : "추가"}
                </Button>
                {editingId ? (
                  <Button color="soft-secondary" onClick={resetForm} disabled={saveMutation.isPending}>
                    <X size={16} className="me-1" />
                    취소
                  </Button>
                ) : null}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={8}>
          <Card>
            <CardHeader>옵션 목록</CardHeader>
            <CardBody>
              {optionQuery.isLoading ? (
                <div className="py-5 text-center text-muted">단체예약 옵션을 불러오는 중입니다.</div>
              ) : optionQuery.isError ? (
                <div className="py-5 text-center text-danger">단체예약 옵션을 불러오지 못했습니다.</div>
              ) : options.length === 0 ? (
                <div className="py-5 text-center text-muted">등록된 단체예약 옵션이 없습니다.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>옵션명</th>
                        <th>주중 가격</th>
                        <th>주말 가격</th>
                        <th>노출</th>
                        <th>정렬</th>
                        <th className="text-end">액션</th>
                      </tr>
                    </thead>
                    <tbody>
                      {options.map((option) => (
                        <tr key={option.id}>
                          <td>
                            <div className="fw-semibold">{option.name}</div>
                            <div className="text-muted fs-12">{option.description || "-"}</div>
                          </td>
                          <td>{formatAmount(option.weekday_price)}</td>
                          <td>{formatAmount(option.weekend_price)}</td>
                          <td>
                            <Badge color={option.is_active ? "success" : "secondary"}>
                              {option.is_active ? "노출" : "숨김"}
                            </Badge>
                          </td>
                          <td>{option.sort_order}</td>
                          <td className="text-end">
                            <Button size="sm" color="soft-primary" className="me-2" onClick={() => startEdit(option)}>
                              <Edit2 size={14} className="me-1" />
                              수정
                            </Button>
                            <Button
                              size="sm"
                              color="soft-danger"
                              disabled={deleteMutation.isPending}
                              onClick={async () => {
                                if (await dialogService.confirm("단체예약 옵션을 삭제하시겠습니까?")) {
                                  deleteMutation.mutate(option.id);
                                }
                              }}
                            >
                              <Trash2 size={14} className="me-1" />
                              삭제
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </GroupReservationPageShell>
  );
}
